import { z } from 'zod'

/**
 * Web-push subscription schemas (Issue #484).
 *
 * Owns the request/response shapes for registering and removing a browser
 * Push API subscription. A subscription is the serialized `PushSubscription`
 * object returned by the browser's `PushManager.subscribe()` plus the two
 * encryption keys (`p256dh`, `auth`) the backend needs to encrypt payloads.
 *
 * NOTE: This is the *web-push* subscription, distinct from the Stripe billing
 * subscription owned by `subscription.contract.ts` / `schemas/subscription.ts`.
 */

/**
 * The two base64url-encoded keys the browser exposes for payload encryption.
 * Both are required by the web-push protocol.
 */
export const PushSubscriptionKeysSchema = z.object({
  p256dh: z.string().min(1),
  auth: z.string().min(1),
})
export type PushSubscriptionKeys = z.infer<typeof PushSubscriptionKeysSchema>

/**
 * Upsert request: the serialized browser PushSubscription.
 * `endpoint` is the unique push-service URL and is the natural upsert key.
 */
export const SubscribePushRequestSchema = z.object({
  endpoint: z.string().url(),
  keys: PushSubscriptionKeysSchema,
  /** Optional user-agent string for multi-device disambiguation in the UI. */
  ua: z.string().max(512).optional(),
})
export type SubscribePushRequest = z.infer<typeof SubscribePushRequestSchema>

export const SubscribePushResponseSchema = z.object({
  subscribed: z.literal(true),
  /** Stable id of the stored subscription row. */
  id: z.string().uuid(),
})
export type SubscribePushResponse = z.infer<typeof SubscribePushResponseSchema>

/**
 * Unsubscribe request: identify the subscription to remove by its endpoint.
 * Deleting by endpoint (rather than id) lets the client unsubscribe even when
 * it never persisted the server-side row id.
 */
export const UnsubscribePushRequestSchema = z.object({
  endpoint: z.string().url(),
})
export type UnsubscribePushRequest = z.infer<typeof UnsubscribePushRequestSchema>

export const UnsubscribePushResponseSchema = z.object({
  unsubscribed: z.literal(true),
  /** How many rows were removed (0 if the endpoint was already gone). */
  removed_count: z.number().int().min(0),
})
export type UnsubscribePushResponse = z.infer<typeof UnsubscribePushResponseSchema>
